const clean = (value) => String(value ?? '').trim();

export const getItemExternalCode = (item = {}) => clean(
  item.externalCode || item.itemExternalCode || item.optionExternalCode
);

export const getProductExternalCode = (item = {}) => clean(
  item.productExternalCode
  || item.raw?.product?.externalCode
  || item.product?.externalCode
);

const mutationPayload = (result = {}) => {
  if (result?.data?.confirmed !== undefined) return result.data;
  return result;
};

export const requireConfirmedItemExternalCode = (result, expected = {}) => {
  const payload = mutationPayload(result);
  const expectedItemId = clean(expected.catalogItemId);
  const expectedCode = clean(expected.externalCode);
  if (
    payload?.success !== true
    || payload?.confirmed !== true
    || clean(payload.catalogItemId) !== expectedItemId
    || clean(payload.externalCode) !== expectedCode
  ) {
    throw new Error('O backend não confirmou autoritativamente o código PDV do Item no iFood.');
  }
  return payload;
};

export const requireConfirmedOptionExternalCode = (result, expected = {}) => {
  const payload = mutationPayload(result);
  const expectedItemId = clean(expected.catalogItemId);
  const expectedOptionId = clean(expected.optionId);
  const expectedCode = clean(expected.externalCode);
  if (
    payload?.success !== true
    || payload?.confirmed !== true
    || clean(payload.catalogItemId) !== expectedItemId
    || clean(payload.resourceId || payload.optionId) !== expectedOptionId
    || clean(payload.externalCode) !== expectedCode
  ) {
    throw new Error('O backend não confirmou autoritativamente o código PDV da Option no iFood.');
  }
  return payload;
};

const getFlatRoot = (flat = {}) => flat.itemFlat || flat.data || flat;

export const requireItemProductIdentity = (flat, expected = {}) => {
  const root = getFlatRoot(flat);
  const item = root.item || {};
  const expectedItemId = clean(expected.catalogItemId);
  const expectedProductId = clean(expected.productId);
  const product = (Array.isArray(root.products) ? root.products : []).find((entry) => (
    clean(entry?.id || entry?.productId) === expectedProductId
  ));
  if (
    clean(item.id) !== expectedItemId
    || clean(item.productId) !== expectedProductId
    || !product
  ) {
    throw new Error('O GET flat não confirmou a relação canônica entre Item e Product.');
  }
  return { item, product, flat: root };
};

export const findFlatOption = (flat = {}, optionId) => {
  const root = getFlatRoot(flat);
  const targetId = clean(optionId);
  const directOptions = Array.isArray(root.options) ? root.options : [];
  const nestedGroups = [
    ...(Array.isArray(root.item?.itemOptionGroups) ? root.item.itemOptionGroups : []),
    ...(Array.isArray(root.item?.optionGroups) ? root.item.optionGroups : []),
  ];
  return [
    ...directOptions,
    ...nestedGroups.flatMap((group) => (Array.isArray(group.options) ? group.options : [])),
  ].find((option) => clean(option?.id || option?.optionId) === targetId) || null;
};

export const getEffectiveContextCodes = (entity = {}) => {
  const rootCode = getItemExternalCode(entity);
  const contextCodes = { ROOT: rootCode };
  (Array.isArray(entity.contextModifiers) ? entity.contextModifiers : []).forEach((modifier) => {
    const catalogContext = clean(modifier?.catalogContext).toUpperCase();
    if (!catalogContext) return;
    contextCodes[catalogContext] = getItemExternalCode(modifier) || rootCode;
  });
  return contextCodes;
};

export const requireEffectiveContextCodes = (entity, expectedCodes = {}) => {
  const actual = getEffectiveContextCodes(entity);
  const expected = Object.fromEntries(Object.entries(expectedCodes || {}).map(([context, code]) => (
    [clean(context).toUpperCase(), clean(code)]
  )));
  const stableEntries = (value) => Object.entries(value).sort(([left], [right]) => left.localeCompare(right));
  if (JSON.stringify(stableEntries(actual)) !== JSON.stringify(stableEntries(expected))) {
    throw new Error('A releitura do iFood não confirmou os contextos efetivos do código PDV.');
  }
  return actual;
};

export const requireItemExternalCodeReadback = (flat, expected = {}) => {
  const identity = requireItemProductIdentity(flat, expected);
  const { item, product, flat: root } = identity;
  const expectedCode = clean(expected.externalCode);
  if (getItemExternalCode(item) !== expectedCode) {
    throw new Error('A releitura do iFood não confirmou o código PDV no Item esperado.');
  }
  const expectedProductCode = clean(expected.productExternalCode);
  if (clean(product.externalCode || product.productExternalCode) !== expectedProductCode) {
    throw new Error('A releitura do iFood não preservou o Product associado ao Item.');
  }
  return { item, product, flat: root };
};

export const requireOptionExternalCodeReadback = (flat, expected = {}) => {
  const root = getFlatRoot(flat);
  const option = findFlatOption(flat, expected.optionId);
  if (!option || getItemExternalCode(option) !== clean(expected.externalCode)) {
    throw new Error('A releitura do iFood não confirmou o código PDV do complemento esperado.');
  }
  const expectedParentProductId = clean(expected.parentProductId);
  if (expectedParentProductId) {
    const parentProduct = (Array.isArray(root.products) ? root.products : []).find((entry) => (
      clean(entry?.id || entry?.productId) === expectedParentProductId
    ));
    if (
      !parentProduct
      || clean(parentProduct.externalCode || parentProduct.productExternalCode) !== clean(expected.parentProductExternalCode)
    ) {
      throw new Error('A releitura do iFood não preservou o Product pai da Option.');
    }
  }
  return option;
};
